import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
    Box,
    Paper,
    Typography,
    Popover,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemText,
    CircularProgress,
    Popper,
    ClickAwayListener,
    Fade
} from '@mui/material'
import { GoVersions, GoChevronDown } from "react-icons/go"
import { MdHistory } from "react-icons/md"
import CloseIcon from '@mui/icons-material/Close'
import { useVirtualizer } from '@tanstack/react-virtual'
import { BsFiletypeJson } from "react-icons/bs"
import { BsFiletypeXml } from "react-icons/bs"
import { MdChangeHistory } from "react-icons/md"

import { TypeVersion, Version, ImageData } from '../../../utils/types'
import { post, fetchPost } from '../../../utils/http'
import { Dotpulse } from '../../common/Loaders'


interface HistoryProps {
    imageData: ImageData
    sourceData: TypeVersion | null
    activeVersion: number
    openHistory: boolean
    anchorElHistory: any
    handleCloseHistory: Function
    handleVersionChange: Function
    isMarkerLoading: boolean
}

const EditHistory = ({
    imageData,
    sourceData,
    activeVersion,
    openHistory,
    anchorElHistory,
    handleCloseHistory,
    handleVersionChange,
    isMarkerLoading
}: HistoryProps) => {
    const [versionHistory, setVersionHistory] = useState<Version[]>([])
    const [isFileTransfer, setIsFileTransfer] = useState<boolean>(false)
    const [page, setPage] = useState<number>(1)
    const [pageEnd, setPageEnd] = useState<boolean>(false)

    const parentRef = useRef<HTMLDivElement>(null)
    const isFetchingRef = useRef(false)
    const isVisibleRef = useRef(false)
    const observer = useRef<IntersectionObserver | null>(null)

    const per_page = 50

    const rowVirtualizer = useVirtualizer({
        count: versionHistory.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 64,
        overscan: 6
    })

    const getHistory = async () => {
        if (!sourceData) return

        isFetchingRef.current = true
        try {
            let res = []
            if (imageData.input_s3 && imageData.input_s3 === 'master-dataset') {
                res = await post(`Annotations/versions/${sourceData.image_id}`, {
                    sys_marker_ver: sourceData.sys_marker_ver,
                    per_page: per_page,
                    page: page
                })
            } else {
                // Slides in user directory (My Folder/Mounts/Shared) will only show
                // versions from their own and the users the slide shared with
                const userIds: number[] = [...new Set([...imageData.uploaded_by, ...imageData.shared])]
                res = await post(`Annotations/versions-by-user/${sourceData.image_id}`, {
                    sys_marker_ver: sourceData.sys_marker_ver,
                    user_ids: userIds,
                    per_page: per_page,
                    page: page
                })
            }

            const formattedRes = res.map((item: Version) => {
                const createdOn = item.adddate ? new Date(item.adddate.replace(' ', 'T')) : null
                const displayDate = createdOn && !isNaN(createdOn.getTime())
                    ? createdOn.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '--'
    
                return { ...item, displayDate }
            })

            if (res.length < per_page) {
                if (observer.current) observer.current.disconnect()
                isVisibleRef.current = false
                setPageEnd(true)
            }

            if (res.length) setVersionHistory(prevList => [...prevList, ...formattedRes])
        } catch (err) {
            console.log(err)
        } finally {
            isFetchingRef.current = false
            // Wait for new items to render
            // If the marker is still visible, trigger the next page
            setTimeout(() => { if (isVisibleRef.current) handleShowMore() }, 150)
        }
    }

    useEffect(() => {
        getHistory()
    }, [page])    // eslint-disable-line

    // Warn user about downloading when navigating away from the page
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {if (isFileTransfer) e.preventDefault()}
        if (isFileTransfer) window.addEventListener('beforeunload', handleBeforeUnload)
        else window.removeEventListener('beforeunload', handleBeforeUnload)
        return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [isFileTransfer])

    const handleClose = () => {
        if (!isFileTransfer) handleCloseHistory()
    }

    const handleShowMore = () => {
        if (isFetchingRef.current) return
        setPage(prevPage => prevPage += 1)
    }

    const markerRef = useCallback((node) => {
        if (observer.current) observer.current.disconnect()
        
        if (node) {
            observer.current = new IntersectionObserver((entries) => {
                isVisibleRef.current = entries[0].isIntersecting
                if (isVisibleRef.current) handleShowMore()
            }, { threshold: 0.1 })
            observer.current.observe(node)
        }
    }, [])

    const downloadFileVersion = async (data: TypeVersion, fileType: string) => {
        setIsFileTransfer(true)
        try {
            const endpoint = fileType === 'xml' ? 'Annotations/download-xml-version' : 'Annotations/download-json-version'
            const res = await fetchPost(endpoint, {version_id: data.id})
            if (!res.ok || !res.body) throw Error(`Failed to download file`)
            
            const blob = await res.blob()
            const downloadUrl = window.URL.createObjectURL(blob)
            const filename = imageData.name.split('/').pop()?.replace(/\.(?=[^.]*$)/, '_')
            
            const a = document.createElement('a')
            a.href = downloadUrl
            a.download = `${filename}.${fileType}`
            document.body.appendChild(a)
            a.click()
            a.remove()
            
            window.URL.revokeObjectURL(downloadUrl)
        } catch (err) {
            console.log(err)
        } finally {
            setIsFileTransfer(false)
        }
    }

    return (
        <Popover
            open={openHistory}
            anchorEl={anchorElHistory}
            onClose={handleClose}
            anchorOrigin={{vertical: 'top', horizontal: 'right'}}
            transformOrigin={{vertical: 'top', horizontal: 'left'}}
            marginThreshold={16}
            slotProps={{
                paper: {
                    sx: {
                        maxHeight: 'calc(100vh - 32px)',
                        width: { xs: '100vw', sm: 300 },
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: 6,
                        borderRadius: 2,
                        overflow: 'hidden',
                    }
                }
            }}
        >
            <Box sx={{
                p: 2,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}>
                <MdHistory color='grey' size={28} />
                <IconButton size="small" onClick={handleClose} aria-label="close">
                    <CloseIcon />
                </IconButton>
            </Box>
            
            <Box ref={parentRef} sx={{position: 'relative', flexGrow: 1, overflowY: 'auto'}}>
                <Box
                    component="ul"
                    sx={{
                        m: 0,
                        p: 0,
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative',
                        listStyle: 'none'
                    }}
                >
                    {rowVirtualizer.getVirtualItems().map(virtualRow => {
                        const item = versionHistory[virtualRow.index]

                        return (
                            <ListItem 
                                key={virtualRow.index} 
                                disablePadding
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: `${virtualRow.size}px`,
                                    transform: `translateY(${virtualRow.start}px)`,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                <ListItemButton
                                    role={undefined}
                                    onClick={() => handleVersionChange(item)}
                                    sx={{height: '100%', width: '100%'}}
                                    selected={item.id === activeVersion}
                                    dense
                                >
                                    <ListItemText
                                        primary={<MdChangeHistory />}
                                        primaryTypographyProps={item.id === activeVersion
                                            ? { color: 'primary.main', fontWeight: 'bold' }
                                            : {}
                                        }
                                        secondary={
                                            <Box 
                                                component="span" 
                                                sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <Typography component="span" variant="body2" color="text.secondary" noWrap sx={{pr: 1}}>
                                                    ID: {item.id}
                                                </Typography>
                                                <Typography component="span" variant="body2" color="text.secondary" noWrap sx={{flexShrink: 0}}>
                                                    {item.displayDate}
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ m: 0 }}
                                    />
                                </ListItemButton>
                                <ListItemButton
                                    role={undefined}
                                    onClick={() => downloadFileVersion(item, 'xml')}
                                    selected={item.id === activeVersion}
                                    className='h-100 d-flex flex-column justify-content-center'
                                    title='Download XML'
                                    dense
                                >
                                    <BsFiletypeXml size={20} />
                                </ListItemButton>
                                <ListItemButton
                                    role={undefined}
                                    onClick={() => downloadFileVersion(item, 'geojson')}
                                    selected={item.id === activeVersion}
                                    className='h-100 d-flex flex-column justify-content-center'
                                    title='Download JSON'
                                    dense
                                >
                                    <BsFiletypeJson size={20} />
                                </ListItemButton>
                            </ListItem>
                        )
                    })}
                </Box>

                {!pageEnd &&
                    <Box ref={markerRef} className='d-flex justify-content-center'>
                        <Dotpulse width={70} height={63} />
                    </Box>
                }
            </Box>
            
            {(isFileTransfer || isMarkerLoading) &&
                <Box
                    className='position-absolute d-flex align-items-center justify-content-center h-100 w-100 p-3'
                    sx={{top: 0, left: 0, background: 'rgba(255, 255, 255, 0.5)'}}
                >
                    <CircularProgress size={32} />
                </Box>
            }
        </Popover>
    )
}

interface VersionProps {
    imageData: ImageData
    activeVerId: number
    sysMarkerVer: number
    versions: TypeVersion[]
    isMarkerLoading: boolean
    handleVersionChange: Function
}

const Versions = ({ imageData, activeVerId, sysMarkerVer, versions, isMarkerLoading, handleVersionChange }: VersionProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [anchorElHistory, setAnchorElHistory] = useState<HTMLButtonElement | null>(null)
    const [selectedSource, setSelectedSource] = useState<TypeVersion | null>(null)
    const [expandedId, setExpandedId] = useState<number | false>(false)

    const isHistoryOpenRef = useRef(false)
  
    const open = Boolean(anchorEl)
    const openHistory = Boolean(anchorElHistory)

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
        setExpandedId(false)
    }
    const handleClose = () => {
        // Prevent closing the main menu if the EditHistory is open
        if (isHistoryOpenRef.current) return
        // remove the focus from any selected accordion before removing the component to avoid browser warning
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur() 
        setAnchorEl(null)
    }

    const handleEditHistory = (event: React.MouseEvent<HTMLButtonElement>, data: TypeVersion) => {
        setSelectedSource(data)
        setAnchorElHistory(event.currentTarget)
        isHistoryOpenRef.current = true
    }
    const handleCloseHistory = () => {
        if (isMarkerLoading) return
        setAnchorElHistory(null)
        setTimeout(() => { isHistoryOpenRef.current = false }, 150)
    }

    const onSelectVersion = (version: TypeVersion) => {
        handleVersionChange(version)
        handleClose()
    }

    const handleAccordionChange = (id: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedId(isExpanded ? id : false)
    }

    return (
        <Box sx={{
            display: 'inline-block',
            borderRadius: 2,
            border: '2px solid #ccc',
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(8px)"
        }}>
            <Box className="d-flex align-items-center gap-2 px-3 py-2" onClick={handleClick} sx={{ cursor: 'pointer' }}>
                <GoVersions size={18} />
                <Typography sx={{ fontWeight: 400 }}>v{activeVerId}</Typography>
            </Box>

            <Popper
                open={open}
                anchorEl={anchorEl}
                placement="right-end"
                transition
                modifiers={[
                    {
                        name: 'flip',
                        enabled: true,
                        options: { fallbackPlacements: ['right-end', 'top-start'] }
                    },
                    {
                        name: 'preventOverflow',
                        enabled: true,
                        options: { boundary: 'viewport', padding: 16 }
                    }
                ]}
                style={{ zIndex: 1300 }}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Fade {...TransitionProps} timeout={250}>
                            <Paper
                                elevation={8}
                                sx={{
                                    width: 320,
                                    maxHeight: 'calc(100vh - 90px)',
                                    overflowY: 'auto',
                                    borderRadius: 2
                                }}
                            >
                                <Box sx={{ 
                                    p: 2,
                                    borderBottom: '1px solid #e0e0e0',
                                    bgcolor: 'background.paper',
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 2
                                }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                                    >
                                        Annotations
                                    </Typography>
                                </Box>

                                <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
                                    {versions.map((i: TypeVersion, index: number) => {
                                        const versionNum = versions.length - index
                                        const isSelected = i.sys_marker_ver === sysMarkerVer
                                        const rawDateString = i.xml_data?.adddate
                                        const createdOn = rawDateString ? new Date(rawDateString.replace(' ', 'T')) : null
                                        const displayDate = createdOn && !isNaN(createdOn.getTime())
                                            ? createdOn.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                            : '--'

                                        return (
                                            <Accordion 
                                                key={i.id}
                                                expanded={expandedId === i.id}
                                                onChange={handleAccordionChange(i.id)}
                                                disableGutters 
                                                elevation={0}
                                                square
                                                sx={{
                                                    borderBottom: '1px solid #f0f0f0',
                                                    bgcolor: isSelected ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                                                    '&:before': { display: 'none' }
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<GoChevronDown />}
                                                    sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}
                                                >
                                                    <Box>
                                                        <Typography sx={{
                                                            fontWeight: isSelected ? 600 : 400,
                                                            color: isSelected ? 'primary.main' : 'text.primary'
                                                        }}>
                                                            Base: {versionNum}
                                                        </Typography>
                                                        {isSelected && (
                                                            <Typography variant="caption" color="primary" sx={{ display: 'block', mt: -0.5 }}>
                                                                Currently viewing
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </AccordionSummary>

                                                <AccordionDetails sx={{ p: 2, pt: 0 }}>
                                                    <Box className='d-flex gap-3 flex-column'>
                                                        <Box className='d-flex gap-2 justify-content-between flex-wrap'>
                                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                                ID: {i.sys_marker_ver || '--'}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                                Created: {displayDate || '--'}
                                                            </Typography>
                                                            {/* <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                                                Created By: {i.xml_data?.user_id || '--'}
                                                            </Typography> */}
                                                            <Typography variant="body2" color="text.secondary" sx={{ 
                                                                lineHeight: 1.6,
                                                                wordBreak: 'break-word',
                                                                overflowWrap: 'anywhere',
                                                            }}>
                                                                File: {i.xml_data?.xml_name || '--'}
                                                            </Typography>
                                                        </Box>
                                                        <Button 
                                                            variant={isSelected ? "outlined" : "contained"} 
                                                            size="small" 
                                                            fullWidth
                                                            disableElevation
                                                            disabled={isSelected}
                                                            onClick={() => onSelectVersion(i)}
                                                        >
                                                            {isSelected ? 'Active' : 'Open'}
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            size="small" 
                                                            fullWidth
                                                            disableElevation
                                                            onClick={(e) => handleEditHistory(e, i)}
                                                        >
                                                            Show edits history
                                                        </Button>
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        )
                                    })}
                                </Box>
                            </Paper>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
            {openHistory &&
                <EditHistory
                    imageData={imageData}
                    sourceData={selectedSource}
                    activeVersion={activeVerId}
                    openHistory={openHistory}
                    anchorElHistory={anchorElHistory}
                    handleCloseHistory={handleCloseHistory}
                    handleVersionChange={handleVersionChange}
                    isMarkerLoading={isMarkerLoading}
                />
            }
        </Box>
    )
}

export default Versions